import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addMetadataElementsToCanvas as addMetadataElementsToCanvasAction,
  connectSfStages,
  selectIsFunnelMapEmpty,
} from '../../../../reducers/united-canvas/unitedCanvasReducer';
import {
  setIsLoadingCursor,
  setTransientFunnel,
} from '../../../../reducers/canvasPageStateReducer';
import { FunnelType } from '../../../../types/enums/FunnelType';
import { getFullMetadataElementId } from './metadataElementUtils';
import { useFunnelMapApiFacade } from '../../../../apis/facades/funnel-map/useFunnelMapApiFacade';
import { telemetry } from '../../../../telemetry';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import {
  EnrichedMetadataElement,
  EnrichContext,
  EnrichContextType,
  EnrichTarget,
  EnrichTargetType,
  MetadataConnectionType,
} from '@server/metadata-elements';
import { StepToMetadataConnection } from '../../../funnel-map-canvas/funnelMapCanvasTypes';

export type FunnelMapPosition = {
  column: number;
  row: number;
};

export type FunnelMapMetadataElement = EnrichedMetadataElement & {
  position?: FunnelMapPosition;
  parentType: string;
};

export type CanvasConnection = {
  source: string;
  target: string;
  targetType: FunnelType;
};

export type MetadataElementWithoutConnections = Omit<
  FunnelMapMetadataElement,
  'dependencyConnections' | 'usedByConnections'
>;

const DEFAULT_POSITION: FunnelMapPosition = { column: 0, row: 0 };

export const addDefaultPositions = (metadataElements: FunnelMapMetadataElement[]) => {
  return metadataElements.map((element) => ({
    name: element.name,
    type: element.type,
    crmOrgId: element.crmOrgId,
    metadataId: element.metadataId,
    position: element.position || DEFAULT_POSITION,
    color: element.color,
    icon: element.icon,
  }));
};

export const assignFunnelMapConnectionsFromMetadataElements = (
  metadataElements: FunnelMapMetadataElement[],
  canvasFunnelMap: FunnelMap,
): CanvasConnection[] => {
  const allowedConnections = Object.values(canvasFunnelMap.metadataElements).flatMap(
    (metadataElement) => ({
      crmOrgId: metadataElement.crmOrgId,
      metadataId: metadataElement.metadataId,
    }),
  );
  allowedConnections.push(
    ...metadataElements.map((metadataElement) => ({
      crmOrgId: metadataElement.crmOrgId,
      metadataId: metadataElement.metadataId,
    })),
  );

  const connections = metadataElements.flatMap((metadataElement) => {
    const usedByConnections = createUsedByConnectionsExcludingSteps(metadataElement);
    const dependenciesConnections = createDependenciesConnections(metadataElement);
    return [...usedByConnections, ...dependenciesConnections];
  });
  return connections.filter(
    (connection) =>
      allowedConnections.map(getFullMetadataElementId).includes(connection.source) &&
      allowedConnections.map(getFullMetadataElementId).includes(connection.target),
  );
};

const createUsedByConnectionsExcludingSteps = (
  metadataElement: FunnelMapMetadataElement,
): CanvasConnection[] => {
  return (
    metadataElement.usedByConnections
      ?.filter((connection) => connection.type === MetadataConnectionType.MetadataElement)
      .map((connection) => ({
        source: getFullMetadataElementId(connection),
        target: getFullMetadataElementId(metadataElement),
        targetType: FunnelType.METADATA_ELEMENT,
      })) ?? []
  );
};

const extractStepConnections = (
  metadataElement: FunnelMapMetadataElement,
): StepToMetadataConnection[] => {
  const targetMetadataElementId = getFullMetadataElementId(metadataElement);

  return (
    metadataElement.usedByConnections
      ?.filter((connection) => connection.type === MetadataConnectionType.FunnelStep)
      .map((connection) => ({
        funnelId: connection.funnelId,
        stageId: connection.stageId,
        targetMetadataElementId,
      })) ?? []
  );
};

const createDependenciesConnections = (
  metadataElement: FunnelMapMetadataElement,
): CanvasConnection[] => {
  return (
    metadataElement.dependencyConnections?.map((connection) => ({
      source: getFullMetadataElementId(metadataElement),
      target: getFullMetadataElementId(connection),
      targetType: FunnelType.METADATA_ELEMENT,
    })) ?? []
  );
};

export const useAddMetadataElementToCanvas = (skipLocating?: boolean) => {
  const dispatch = useDispatch();
  const isFunnelMapEmpty = useSelector(selectIsFunnelMapEmpty);

  const addMetadataElementsToCanvas = useCallback(
    (metadataElements: FunnelMapMetadataElement[], canvasFunnelMap: FunnelMap) => {
      const metadataElementsWithPosition = addDefaultPositions(metadataElements);

      const connections = assignFunnelMapConnectionsFromMetadataElements(
        metadataElements,
        canvasFunnelMap,
      );

      const allStepConnections = metadataElements.flatMap(extractStepConnections);
      const stepConnectionsWithExistingFunnels = allStepConnections.filter(
        (stepConnection) =>
          canvasFunnelMap.funnelsData?.[stepConnection.funnelId] !== undefined &&
          stepConnection.stageId !== '',
      );

      const shouldAddDirectly = isFunnelMapEmpty || skipLocating;

      const action = shouldAddDirectly
        ? addMetadataElementsToCanvasAction({
            metadataElements: metadataElementsWithPosition,
            connections,
          })
        : setTransientFunnel({
            type: FunnelType.METADATA_ELEMENT,
            importType: 'import',
            data: {
              metadataElements: metadataElementsWithPosition,
              connections,
              stepConnections: stepConnectionsWithExistingFunnels,
            },
            isPlacingFunnel: true,
          });

      dispatch(action);

      const shouldDispatchStepConnectionsImmediately = shouldAddDirectly;
      if (shouldDispatchStepConnectionsImmediately) {
        stepConnectionsWithExistingFunnels.forEach((stepConnection) => {
          dispatch(
            connectSfStages({
              stageA: {
                funnelId: stepConnection.funnelId,
                stageId: stepConnection.stageId,
              },
              stageB: {
                funnelId: stepConnection.targetMetadataElementId,
                stageId: undefined,
                funnelType: FunnelType.METADATA_ELEMENT,
              },
            }),
          );
        });
      }
    },
    [dispatch, isFunnelMapEmpty, skipLocating],
  );

  const { post_enrichMetadataElements } = useFunnelMapApiFacade();
  const { addNotification } = useSweepNotifications();

  const getMetadataElementsWithConnections = useCallback(
    async ({
      metadataElements,
      parentElement,
      commonChild,
      funnelMapId,
    }: {
      metadataElements: MetadataElementWithoutConnections[];
      parentElement?: {
        metadataId: string;
        crmOrgId: string;
      };
      commonChild?: {
        metadataId: string;
        crmOrgId: string;
      };
      funnelMapId?: string;
    }) => {
      if (metadataElements.length === 0) {
        return [];
      }

      const crmOrgId = metadataElements[0].crmOrgId;
      if (!metadataElements.every((el) => el.crmOrgId === crmOrgId)) {
        throw new Error('All metadata elements must belong to the same crmOrgId');
      }

      const targets = metadataElements.map(
        (el): EnrichTarget => ({
          type: EnrichTargetType.MetadataElement,
          metadataElementId: getFullMetadataElementId(el),
        }),
      );

      const parentElementId = parentElement ? getFullMetadataElementId(parentElement) : undefined;
      const commonChildId = commonChild ? getFullMetadataElementId(commonChild) : undefined;

      const context: EnrichContext[] | undefined = funnelMapId
        ? [{ type: EnrichContextType.FunnelMap, funnelMapId }]
        : undefined;

      const enrichedElements = await post_enrichMetadataElements(crmOrgId, {
        targets,
        context,
        parentElementId,
        commonChildId,
      });

      const originalElementsMap = new Map(
        metadataElements.map((el) => [getFullMetadataElementId(el), el]),
      );

      const metadataElementsWithConnections: FunnelMapMetadataElement[] = enrichedElements.map(
        (enriched) => {
          const fullId = getFullMetadataElementId(enriched);
          const original = originalElementsMap.get(fullId);

          const { dependencyConnections, usedByConnections, ...rest } = enriched;
          return {
            ...rest,
            dependencyConnections,
            usedByConnections,
            position: original?.position,
            parentType: original?.parentType || enriched.type,
          };
        },
      );

      return metadataElementsWithConnections;
    },
    [post_enrichMetadataElements],
  );

  const addMetadataElementsToCanvasAndEnrichConnections = useCallback(
    async ({
      metadataElements,
      canvasFunnelMap,
      parentElement,
      commonChild,
    }: {
      metadataElements: MetadataElementWithoutConnections[];
      canvasFunnelMap: FunnelMap;
      parentElement?: {
        metadataId: string;
        crmOrgId: string;
      };
      commonChild?: {
        metadataId: string;
        crmOrgId: string;
      };
    }) => {
      dispatch(setIsLoadingCursor(true));
      try {
        const metadataElementsWithConnections = await getMetadataElementsWithConnections({
          metadataElements,
          parentElement,
          commonChild,
          funnelMapId: canvasFunnelMap.id,
        });

        addMetadataElementsToCanvas(metadataElementsWithConnections, canvasFunnelMap);
      } catch (error) {
        telemetry.captureError(error);
        addNotification({
          message: 'Failed to add metadata elements to canvas',
          variant: SweepNotificationVariant.Error,
        });
      } finally {
        dispatch(setIsLoadingCursor(false));
      }
    },
    [addMetadataElementsToCanvas, addNotification, dispatch, getMetadataElementsWithConnections],
  );

  return {
    addMetadataElementsToCanvasAndEnrichConnections,
    getMetadataElementsWithConnections,
  };
};
